import React, { useEffect, useState } from 'react';
import { Box, keyframes } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';

// Float and rotate animation for papers
const floatPaper = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) rotate(var(--r));
    opacity: 0;
  }
`;

// Pulse for search area
const searchPulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.7;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.5;
  }
`;

/**
 * PapersSearchAnimation - Shows papers flying around during search
 * @param {Object} location - { lat, lng } search location
 * @param {boolean} active - Whether animation is active
 * @param {Object} mapRef - Reference to map
 */
function PapersSearchAnimation({ location, active, mapRef }) {
  const [position, setPosition] = useState(null);
  const [papers, setPapers] = useState([]);

  useEffect(() => {
    if (active && location && mapRef) {
      const map = mapRef.getMap();
      if (!map) return;

      // Use Google Maps overlay to get screen coordinates
      const overlay = new window.google.maps.OverlayView();
      overlay.draw = function() {};
      overlay.onAdd = function() {
        const projection = this.getProjection();
        if (projection) {
          const latLng = new window.google.maps.LatLng(location.lat, location.lng);
          const point = projection.fromLatLngToContainerPixel(latLng);

          if (point) {
            setPosition({ x: point.x, y: point.y });

            // Generate random papers
            const paperCount = 8;
            const generatedPapers = [];
            for (let i = 0; i < paperCount; i++) {
              const angle = (Math.PI * 2 * i) / paperCount + (Math.random() - 0.5) * 0.5;
              const distance = 40 + Math.random() * 30;
              generatedPapers.push({
                id: i,
                tx: Math.cos(angle) * distance,
                ty: Math.sin(angle) * distance,
                rotation: Math.random() * 720 - 360,
                delay: Math.random() * 0.3,
              });
            }
            setPapers(generatedPapers);
          }
        }
      };
      overlay.onRemove = function() {};
      overlay.setMap(map);

      return () => {
        try {
          overlay.setMap(null);
        } catch (e) {
          // Ignore cleanup errors
        }
      };
    }
  }, [active, location, mapRef]);

  if (!active || !position) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 999,
      }}
    >
      {/* Search area circle */}
      <Box
        sx={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: '80px',
          height: '80px',
          marginLeft: '-40px',
          marginTop: '-40px',
          borderRadius: '50%',
          backgroundColor: 'rgba(33, 150, 243, 0.15)',
          border: '2px solid rgba(33, 150, 243, 0.4)',
          animation: `${searchPulse} 1.5s ease-in-out infinite`,
        }}
      />

      {/* Flying papers */}
      {papers.map((paper) => (
        <Box
          key={paper.id}
          sx={{
            position: 'absolute',
            left: position.x,
            top: position.y,
            marginLeft: '-12px',
            marginTop: '-12px',
            '--tx': `${paper.tx}px`,
            '--ty': `${paper.ty}px`,
            '--r': `${paper.rotation}deg`,
            animation: `${floatPaper} 2s ease-out ${paper.delay}s`,
          }}
        >
          <DescriptionIcon
            sx={{
              fontSize: '24px',
              color: 'rgba(33, 150, 243, 0.8)',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
            }}
          />
        </Box>
      ))}

      {/* Center indicator */}
      <Box
        sx={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: '12px',
          height: '12px',
          marginLeft: '-6px',
          marginTop: '-6px',
          borderRadius: '50%',
          backgroundColor: 'rgba(33, 150, 243, 0.8)',
          boxShadow: '0 0 10px rgba(33, 150, 243, 0.6)',
        }}
      />

      {/* Searching text */}
      <Box
        sx={{
          position: 'absolute',
          left: position.x,
          top: position.y + 50,
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          borderRadius: '12px',
          padding: '6px 12px',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box
          sx={{
            color: 'rgba(33, 150, 243, 1)',
            fontWeight: 600,
            fontSize: '11px',
            whiteSpace: 'nowrap',
          }}
        >
          Searching area...
        </Box>
      </Box>
    </Box>
  );
}

export default PapersSearchAnimation;
